import type { BffSubmitResponse } from '@/services/rivaas-api';
import type {
  MultiRangeLivenessChallengeItemValues,
  OnCompleteCallbackImage,
} from '@innovatrics/dot-multi-range-liveness';
import type { FailReasonValues } from '@repo/common';

import { Spacer } from '@dot/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { onAutoCaptureError } from '../utils/error';

import { MultiRangeCamera } from './components/camera';
import { MultiRangeCameraUi } from './components/camera-ui';

import { CaptureServerErrorOverlay } from '@/components/CaptureServerErrorOverlay/CaptureServerErrorOverlay';
import { ManualMessageUpload } from '@/components/ManualUpload/ManualMessageUpload';
import { Processing } from '@/components/Processing/Processing';
import { SecuredByBadge } from '@/components/SecuredByBadge/SecuredByBadge';
import { CaptureWrapper } from '@/components/capture-wrapper';
import { config } from '@/config';
import { useAppContext } from '@/providers/app-provider/context';
import { selectStep } from '@/providers/app-provider/selectors';
import { postSubmitHandled } from '@/services/rivaas-api';
import * as GSC from '@/styles/GlobalStyles';
import { applyBffSubmitResponse, buildSubmitErrorContext, retrySubmitErrorStep } from '@/utils/submit-error';
import { getPublicAsset } from '@/utils/utils';

function readChallengeSequence(data: Record<string, unknown> | undefined): MultiRangeLivenessChallengeItemValues[] {
  const seq = data?.challengeSequence;

  if (!Array.isArray(seq)) {
    return [];
  }

  return seq.filter((s): s is MultiRangeLivenessChallengeItemValues => typeof s === 'string');
}

/**
 * Manifest-driven `multirange_capture` UI step (DOT web `Multi-Range Liveness`).
 *
 * The DIS challenge is pre-issued by BFF in the response that ADVANCES TO this step
 * (look-ahead pattern in `attachLookAheadChallenge`). FE reads `challengeSequence`
 * directly from the current `step.data`, renders the DOT component, and submits the
 * `onComplete` binary `content` (octet-stream for DIS) — single round-trip per phase.
 */
export const MultiRangeLiveness = () => {
  const { t } = useTranslation();
  const { dispatch, state } = useAppContext();
  const currentStep = selectStep(state);
  const challengeSequence = readChallengeSequence(currentStep?.data);
  // Mirrors the passive FaceCapture PROCESSING overlay: frozen last frame + "Processing"
  // text while the submit round-trips (the DOT UI otherwise ends in its own raw state).
  const [processing, setProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const [serverErrorFailReason, setServerErrorFailReason] = useState<FailReasonValues | null>(null);
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const [serverErrorRetryable, setServerErrorRetryable] = useState(false);

  const redirectOptions = {
    unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
    verifiedUrl: state.customerData?.redirect?.verifiedUrl,
  };

  const upload = async (content: Uint8Array): Promise<BffSubmitResponse> => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);

    const blob = new Blob([new Uint8Array(content)], { type: 'application/octet-stream' });
    const data = await postSubmitHandled(blob);

    if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
      const errorContext = buildSubmitErrorContext(data);

      // Same reset the passive step does before its error overlay takes over.
      setProcessing(false);
      setCapturedImage(null);
      setServerErrorFailReason(errorContext?.failReason ?? null);
      setServerErrorMessage(errorContext?.message ?? null);
      setServerErrorRetryable(errorContext?.retryable ?? false);

      return data;
    }

    return data;
  };

  const retryCapture = () => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);
    retrySubmitErrorStep(dispatch, state.submitErrorContext, redirectOptions);
  };

  return (
    <>
      <GSC.RelativeContainer>
        <CaptureWrapper>
          <GSC.CaptureContainer>
            {challengeSequence.length > 0 && (
              <>
                <MultiRangeCamera
                  assetsDirectoryPath={getPublicAsset('/')}
                  challengeSequence={challengeSequence}
                  onComplete={(resultData: OnCompleteCallbackImage, content: Uint8Array) => {
                    // Freeze the final captured frame under the overlay.
                    setCapturedImage(resultData.imageWithMetadata?.image ?? null);
                    setProcessing(true);
                    void upload(content);
                  }}
                  onError={onAutoCaptureError}
                />
                <MultiRangeCameraUi
                  appStateInstructions={{
                    loading: { text: t('common.state.loading'), visible: true },
                    waiting: { text: '', visible: false },
                  }}
                  instructions={{
                    candidate_selection: t('steps.faceCapture.instructions.candidateSelection'),
                    face_centering: t('steps.faceCapture.instructions.faceCentering'),
                    face_too_close: t('steps.faceCapture.instructions.faceTooClose'),
                    face_too_far: t('steps.faceCapture.instructions.faceTooFar'),
                    face_not_present: t('steps.faceCapture.instructions.faceNotPresent'),
                    sharpness_too_low: t('steps.faceCapture.instructions.sharpnessTooLow'),
                    brightness_too_high: t('steps.faceCapture.instructions.brightnessTooHigh'),
                    brightness_too_low: t('steps.faceCapture.instructions.brightnessTooLow'),
                    device_pitched: t('steps.faceCapture.instructions.devicePitched'),
                  }}
                />
              </>
            )}
          </GSC.CaptureContainer>
        </CaptureWrapper>
        {processing && (
          <GSC.FadeInContainer>
            <Processing capturedImage={capturedImage} />
          </GSC.FadeInContainer>
        )}
        {serverErrorFailReason && (
          <CaptureServerErrorOverlay
            failReason={serverErrorFailReason}
            message={serverErrorMessage}
            onRetry={retryCapture}
            retryable={serverErrorRetryable}
            unverifiedUrl={state.customerData?.redirect?.unverifiedUrl ?? ''}
          />
        )}
      </GSC.RelativeContainer>
      <Spacer $top={1}>
        <SecuredByBadge />
      </Spacer>
      {config.photoUploadEnabled && (
        <ManualMessageUpload
          id="multirange_liveness_manual_upload"
          onComplete={(content) => {
            setProcessing(true);
            void upload(content);
          }}
        />
      )}
    </>
  );
};
